import { useQuery } from '@apollo/client';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { CreateProduct } from '@thro-types/leihbar';

import { useFormikContext } from 'formik';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import USERS_BY_EMAIL_QUERY from 'gql/queries/EventService/getUsersEmail.gql';

import { GQLGuard } from 'app/components';
import { UsersQueryArgs } from 'app/containers/AWPM-FWPM/ModuleSelections/models';
import { translations } from 'locales/i18n';
import { UserType } from 'utils/ACLHelper';

interface Options {
  id?: number;
  userId?: number;
  email: string;
}

export const EditorAutocomplete = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Options[]>([]);
  const [loading, setLoading] = useState(false);

  const { setFieldValue, values } = useFormikContext<CreateProduct>();

  const getUsersQuery = useQuery<{ users: Options[] }, UsersQueryArgs>(USERS_BY_EMAIL_QUERY, {
    context: { clientName: 'federation' },
    variables: {
      type: UserType.SHIBBOLETH,
      entitlements: [
        'rp-Gesamt-Dashboard-leihBar-Verleiher',
        'rp-Gesamt-Dashboard-leihBar-Administrator'
      ],
      limit: 0
    }
  });

  useEffect(() => {
    setLoading(getUsersQuery.loading);
    if (!!getUsersQuery.data) {
      setOptions([...getUsersQuery.data?.users]);
    }
  }, [getUsersQuery]);

  const changeHandler = value => {
    let filter: string | undefined = undefined;
    if (value !== '') {
      filter = JSON.stringify({ email: { term: value } });
    }
    getUsersQuery.refetch({ ids: undefined, filter: filter, type: UserType.SHIBBOLETH });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceChangeHandler = useCallback(
    debounce(value => {
      changeHandler(value);
    }, 500),
    []
  );

  const handleChange = (event, value: Options[]) => {
    const editors = value.map(item => ({ userId: item.userId || item.id, email: item.email }));
    return setFieldValue('editors', editors);
  };

  return (
    <GQLGuard query={getUsersQuery}>
      {data => {
        return (
          <Autocomplete
            id="editors"
            fullWidth
            multiple
            defaultValue={[...values.editors.map(editor => editor)]}
            value={values.editors}
            open={open}
            size="small"
            onOpen={() => {
              setOpen(true);
            }}
            onClose={() => {
              setOpen(false);
            }}
            onInputChange={(event, value) => {
              debounceChangeHandler(value);
            }}
            isOptionEqualToValue={(option, value) => {
              //@ts-ignore Id is available. Just the CreateLender Type in CreateProduct is not correct
              return option.id === value.userId;
            }}
            getOptionLabel={option => option.email}
            onChange={handleChange}
            options={options}
            loading={loading}
            renderInput={params => (
              <TextField
                {...params}
                placeholder={t(translations.pages.events.create.organizingPersonsPlaceholder())}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  )
                }}
              />
            )}
          />
        );
      }}
    </GQLGuard>
  );
};
